import { useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { useLanguage } from "../i18n/LanguageProvider";
import { termsData, termsMeta } from "../data/termsTranslations";

export default function TermsConditions() {
  const { language } = useLanguage();
  const articles = termsData[language] || termsData.nl;
  const meta = termsMeta[language] || termsMeta.nl;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDownload = useCallback(() => {
    let text = `${meta.title.toUpperCase()}\n`;
    text += "Website: www.clean-force.eu\n";
    text += `${meta.effective}\n`;
    text += "=".repeat(60) + "\n\n";

    articles.forEach((article) => {
      text += article.title.toUpperCase() + "\n\n";
      article.items.forEach((item, i) => {
        const clean = item.replace(/<strong>/g, "").replace(/<\/strong>/g, "");
        text += `${i + 1}. ${clean}\n\n`;
      });
      text += "\n";
    });

    text += "=".repeat(60) + "\n";
    text += "\u00a9 2026 Clean-Force BV\n";
    text += "KvK 94363803 | BTW NL866749913B01\n";
    text += "Robert Schumandomein 2, 6229ES Maastricht (NL)\n";
    text += "Dorpsstraat 16, 3390 Tielt-Winge (BE)\n";

    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = meta.filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [articles, meta]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="terms-page">
      <div className="max-w-[980px] mx-auto px-5 pt-10 pb-20">
        <Link to="/" className="inline-flex items-center gap-2 text-[#4DD0E1] hover:text-[#3bc0d1] transition-colors mb-6 text-sm" data-testid="terms-back-link">
          <ArrowLeft size={16} /> {meta.back}
        </Link>

        <div className="bg-[#121212] border border-white/10 rounded-3xl p-9 mb-6 shadow-lg">
          <span className="inline-block bg-[#4DD0E1]/10 text-[#4DD0E1] rounded-full px-3.5 py-1.5 text-sm font-bold mb-4">{meta.badge}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-black text-white leading-tight mb-3">{meta.title}</h1>
          <p className="text-base text-[#A1A1AA] max-w-[70ch]">{meta.subtitle}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mt-6">
            {[
              { label: meta.version, value: "2026" },
              { label: meta.application, value: meta.applicationVal },
              { label: "Website", value: "clean-force.eu" },
            ].map((item) => (
              <div key={item.label} className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5">
                <strong className="block text-xs text-[#A1A1AA] mb-1">{item.label}</strong>
                <span className="text-white text-sm">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-3xl p-8 shadow-lg">
          <div className="mb-7 p-5 border border-white/10 rounded-2xl bg-white/[0.02]">
            <h3 className="text-white font-bold text-base mb-3">{meta.tocLabel}</h3>
            <div className="columns-1 sm:columns-2 gap-x-8">
              {articles.map((a) => (
                <p key={a.id} className="mb-1.5">
                  <a href={`#${a.id}`} className="text-[#4DD0E1] hover:text-[#3bc0d1] transition-colors text-sm no-underline">{a.title}</a>
                </p>
              ))}
            </div>
          </div>

          {articles.map((article) => (
            <div key={article.id} id={article.id} className="mb-8 scroll-mt-24">
              <h2 className="text-[#4DD0E1] font-bold text-lg mb-3">{article.title}</h2>
              <ol className="list-decimal pl-6 space-y-2">
                {article.items.map((item, i) => (
                  <li key={i} className="text-[#A1A1AA] text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: item }} />
                ))}
              </ol>
            </div>
          ))}

          <div className="mt-10 pt-6 border-t border-white/10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-white font-bold text-base">{meta.dlTitle}</h3>
                <p className="text-[#A1A1AA] text-sm">{meta.dlDesc}</p>
              </div>
              <button onClick={handleDownload} data-testid="download-terms-btn" className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#4DD0E1]/10 text-[#4DD0E1] hover:bg-[#4DD0E1]/20 transition-colors font-medium text-sm whitespace-nowrap">
                <Download size={18} /> {meta.dlBtn}
              </button>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#A1A1AA]">&copy; 2026 Clean-Force BV</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
